import {
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
  type WheelEvent,
} from "react";

type Point = {
  x: number;
  y: number;
};

type SpatialPoint = Point & {
  z: number;
};

type SpatialDelta = {
  x: number;
  y: number;
  z: number;
};

type ProjectedPoint = Point & {
  depth: number;
};

type FaceSurfacePoint = ProjectedPoint & {
  visibility: number;
  visible: boolean;
};

type HoveredPart = {
  id: string;
  label: string;
  x: number;
  y: number;
};

type SelectedPart = {
  label: string;
  partId: string;
};

type PartHoverHandler = (
  id: string,
  label: string,
  event: PointerEvent<SVGGElement>,
) => void;

type PartSelectHandler = (
  partId: string,
  label: string,
  event: PointerEvent<SVGGElement>,
) => void;

type PoseControls = {
  activeId: string | null;
  editing: boolean;
  onHover: PartHoverHandler;
  onLeave: () => void;
  onSelect: PartSelectHandler;
  selectedPartId: string | null;
};

type StickmanPartProps = PoseControls & {
  children: ReactNode;
  id: string;
  label: string;
  partId: string;
};

type Skeleton = {
  elbowL: Point;
  elbowR: Point;
  footL: Point;
  footR: Point;
  hip: Point;
  kneeL: Point;
  kneeR: Point;
  neck: Point;
  palmL: Point;
  palmR: Point;
  shoulderL: Point;
  shoulderR: Point;
};

type SkeletonDepth = Partial<Record<keyof Skeleton, number>>;

type PoseEdit = {
  depth: SkeletonDepth;
  skeleton: Skeleton;
};

type Pose = {
  depth?: SkeletonDepth;
  key: string;
  label: string;
  skeleton: Skeleton;
};

type Perspective = {
  headOffset: number;
  key: string;
  label: string;
  pitchDeg: number;
  showFace: boolean;
  yawDeg: number;
};

type CharacterFrame = {
  id: string;
  perspective: Perspective;
  pose: Pose;
};

type EditOperation =
  | {
      end: keyof Skeleton;
      joint: keyof Skeleton;
      kind: "bend";
      root: keyof Skeleton;
    }
  | {
      end: keyof Skeleton;
      joint: keyof Skeleton;
      kind: "chain";
      root: keyof Skeleton;
    }
  | {
      kind: "move";
      keys: Array<keyof Skeleton>;
      target: keyof Skeleton;
    };

type EditDrag = {
  partId: string;
  pointerId: number;
  startDepth: SkeletonDepth;
  startPointer: Point;
  startSkeleton: Skeleton;
};

type FigureProps = {
  controls?: PoseControls;
  frame: CharacterFrame;
  interactive?: boolean;
};

type PartGroupProps = FigureProps & {
  children: ReactNode;
  label: string;
  partId: string;
};

const VIEWBOX_W = 422;
const VIEWBOX_H = 560;
const CENTER_X = VIEWBOX_W / 2;
const FACE_RADIUS = 52;
const FACE_EDGE_VISIBILITY = 0.08;
const HEAD_CY = 88;
const CAMERA_PITCH_PIVOT_Y = VIEWBOX_H / 2;
const DEPTH_LIMIT = 150;
const MAX_PITCH = 42;

const SKELETON_KEYS: Array<keyof Skeleton> = [
  "elbowL",
  "elbowR",
  "footL",
  "footR",
  "hip",
  "kneeL",
  "kneeR",
  "neck",
  "palmL",
  "palmR",
  "shoulderL",
  "shoulderR",
];

const PERSPECTIVES: Perspective[] = [
  {
    headOffset: 0,
    key: "front",
    label: "Front",
    pitchDeg: 0,
    showFace: true,
    yawDeg: 0,
  },
  {
    headOffset: 7,
    key: "three-quarter-right",
    label: "3/4 right",
    pitchDeg: 0,
    showFace: true,
    yawDeg: 45,
  },
  {
    headOffset: 20,
    key: "side-right",
    label: "Side right",
    pitchDeg: 0,
    showFace: true,
    yawDeg: 90,
  },
  {
    headOffset: 0,
    key: "back",
    label: "Back",
    pitchDeg: 0,
    showFace: false,
    yawDeg: 180,
  },
  {
    headOffset: -7,
    key: "three-quarter-left",
    label: "3/4 left",
    pitchDeg: 0,
    showFace: true,
    yawDeg: -45,
  },
  {
    headOffset: -20,
    key: "side-left",
    label: "Side left",
    pitchDeg: 0,
    showFace: true,
    yawDeg: -90,
  },
];

const POSES: Pose[] = [
  {
    key: "stand",
    label: "Stand",
    depth: {
      elbowL: -6,
      elbowR: 6,
      footL: -8,
      footR: 8,
      kneeL: -4,
      kneeR: 4,
      palmL: -10,
      palmR: 10,
      shoulderL: -4,
      shoulderR: 4,
    },
    skeleton: {
      neck: { x: 211, y: 190 },
      shoulderL: { x: 184, y: 210 },
      shoulderR: { x: 238, y: 210 },
      elbowL: { x: 181, y: 292 },
      elbowR: { x: 241, y: 292 },
      palmL: { x: 177, y: 370 },
      palmR: { x: 245, y: 370 },
      hip: { x: 211, y: 364 },
      kneeL: { x: 191, y: 446 },
      kneeR: { x: 231, y: 446 },
      footL: { x: 176, y: 501 },
      footR: { x: 246, y: 501 },
    },
  },
  {
    key: "walk-a",
    label: "Walk 1",
    depth: {
      elbowL: 28,
      elbowR: -26,
      footL: -58,
      footR: 54,
      kneeL: -34,
      kneeR: 30,
      palmL: 48,
      palmR: -46,
      shoulderL: 8,
      shoulderR: -8,
    },
    skeleton: {
      neck: { x: 216, y: 188 },
      shoulderL: { x: 188, y: 210 },
      shoulderR: { x: 241, y: 209 },
      elbowL: { x: 173, y: 282 },
      elbowR: { x: 260, y: 278 },
      palmL: { x: 164, y: 360 },
      palmR: { x: 278, y: 350 },
      hip: { x: 214, y: 360 },
      kneeL: { x: 166, y: 423 },
      kneeR: { x: 239, y: 425 },
      footL: { x: 139, y: 501 },
      footR: { x: 273, y: 503 },
    },
  },
  {
    key: "walk-b",
    label: "Walk 2",
    depth: {
      elbowL: -28,
      elbowR: 26,
      footL: 54,
      footR: -58,
      kneeL: 30,
      kneeR: -34,
      palmL: -46,
      palmR: 48,
      shoulderL: -8,
      shoulderR: 8,
    },
    skeleton: {
      neck: { x: 207, y: 188 },
      shoulderL: { x: 181, y: 209 },
      shoulderR: { x: 234, y: 210 },
      elbowL: { x: 160, y: 278 },
      elbowR: { x: 252, y: 282 },
      palmL: { x: 143, y: 350 },
      palmR: { x: 261, y: 360 },
      hip: { x: 208, y: 360 },
      kneeL: { x: 183, y: 425 },
      kneeR: { x: 256, y: 423 },
      footL: { x: 149, y: 503 },
      footR: { x: 285, y: 501 },
    },
  },
  {
    key: "run-a",
    label: "Run 1",
    depth: {
      elbowL: -34,
      elbowR: 30,
      footL: -78,
      footR: 72,
      kneeL: -54,
      kneeR: 44,
      palmL: -62,
      palmR: 55,
      shoulderL: -10,
      shoulderR: 10,
    },
    skeleton: {
      neck: { x: 225, y: 184 },
      shoulderL: { x: 194, y: 207 },
      shoulderR: { x: 247, y: 207 },
      elbowL: { x: 171, y: 260 },
      elbowR: { x: 271, y: 275 },
      palmL: { x: 162, y: 190 },
      palmR: { x: 294, y: 340 },
      hip: { x: 217, y: 360 },
      kneeL: { x: 153, y: 335 },
      kneeR: { x: 252, y: 444 },
      footL: { x: 94, y: 482 },
      footR: { x: 314, y: 365 },
    },
  },
  {
    key: "run-b",
    label: "Run 2",
    depth: {
      elbowL: 30,
      elbowR: -34,
      footL: 72,
      footR: -78,
      kneeL: 44,
      kneeR: -54,
      palmL: 55,
      palmR: -62,
      shoulderL: 10,
      shoulderR: -10,
    },
    skeleton: {
      neck: { x: 219, y: 184 },
      shoulderL: { x: 190, y: 208 },
      shoulderR: { x: 244, y: 207 },
      elbowL: { x: 156, y: 275 },
      elbowR: { x: 266, y: 260 },
      palmL: { x: 135, y: 340 },
      palmR: { x: 279, y: 190 },
      hip: { x: 211, y: 360 },
      kneeL: { x: 179, y: 444 },
      kneeR: { x: 272, y: 335 },
      footL: { x: 118, y: 365 },
      footR: { x: 330, y: 482 },
    },
  },
  {
    key: "jump",
    label: "Jump",
    depth: {
      elbowL: -26,
      elbowR: 26,
      footL: -28,
      footR: 28,
      kneeL: -18,
      kneeR: 18,
      palmL: -44,
      palmR: 44,
      shoulderL: -8,
      shoulderR: 8,
    },
    skeleton: {
      neck: { x: 211, y: 165 },
      shoulderL: { x: 184, y: 188 },
      shoulderR: { x: 238, y: 188 },
      elbowL: { x: 150, y: 144 },
      elbowR: { x: 272, y: 144 },
      palmL: { x: 112, y: 112 },
      palmR: { x: 310, y: 112 },
      hip: { x: 211, y: 320 },
      kneeL: { x: 166, y: 390 },
      kneeR: { x: 256, y: 390 },
      footL: { x: 139, y: 447 },
      footR: { x: 283, y: 447 },
    },
  },
  {
    key: "crouch",
    label: "Crouch",
    depth: {
      elbowL: -18,
      elbowR: 18,
      footL: -35,
      footR: 35,
      kneeL: -42,
      kneeR: 42,
      palmL: -28,
      palmR: 28,
      shoulderL: -8,
      shoulderR: 8,
    },
    skeleton: {
      neck: { x: 211, y: 210 },
      shoulderL: { x: 184, y: 230 },
      shoulderR: { x: 238, y: 230 },
      elbowL: { x: 155, y: 303 },
      elbowR: { x: 267, y: 303 },
      palmL: { x: 140, y: 377 },
      palmR: { x: 282, y: 377 },
      hip: { x: 211, y: 397 },
      kneeL: { x: 159, y: 438 },
      kneeR: { x: 263, y: 438 },
      footL: { x: 126, y: 502 },
      footR: { x: 296, y: 502 },
    },
  },
  {
    key: "wave",
    label: "Wave",
    depth: {
      elbowL: 8,
      elbowR: -35,
      footL: -8,
      footR: 8,
      kneeL: -4,
      kneeR: 4,
      palmL: 12,
      palmR: -58,
      shoulderL: 0,
      shoulderR: -10,
    },
    skeleton: {
      neck: { x: 211, y: 190 },
      shoulderL: { x: 184, y: 210 },
      shoulderR: { x: 238, y: 210 },
      elbowL: { x: 183, y: 293 },
      elbowR: { x: 285, y: 145 },
      palmL: { x: 179, y: 372 },
      palmR: { x: 314, y: 95 },
      hip: { x: 211, y: 364 },
      kneeL: { x: 192, y: 446 },
      kneeR: { x: 232, y: 446 },
      footL: { x: 176, y: 501 },
      footR: { x: 247, y: 501 },
    },
  },
  {
    key: "kick",
    label: "Kick",
    depth: {
      elbowL: 16,
      elbowR: -20,
      footL: 10,
      footR: -88,
      kneeL: 4,
      kneeR: -54,
      palmL: 28,
      palmR: -34,
      shoulderL: 4,
      shoulderR: -8,
    },
    skeleton: {
      neck: { x: 213, y: 188 },
      shoulderL: { x: 185, y: 210 },
      shoulderR: { x: 238, y: 210 },
      elbowL: { x: 168, y: 282 },
      elbowR: { x: 258, y: 270 },
      palmL: { x: 155, y: 356 },
      palmR: { x: 270, y: 326 },
      hip: { x: 213, y: 360 },
      kneeL: { x: 188, y: 444 },
      kneeR: { x: 292, y: 358 },
      footL: { x: 174, y: 501 },
      footR: { x: 356, y: 344 },
    },
  },
];

function StickmanPart({
  activeId,
  children,
  editing,
  id,
  label,
  onHover,
  onLeave,
  onSelect,
  partId,
  selectedPartId,
}: StickmanPartProps) {
  const handleHover = (event: PointerEvent<SVGGElement>) => {
    onHover(id, label, event);
  };

  const handleSelect = (event: PointerEvent<SVGGElement>) => {
    if (event.shiftKey) return;

    onSelect(partId, label, event);
    if (editing) {
      event.currentTarget.setPointerCapture(event.pointerId);
      event.stopPropagation();
    }
  };

  const isActive = activeId === id || selectedPartId === partId;

  return (
    <g
      aria-label={label}
      className={`stickman-part ${isActive ? "is-active" : ""} ${
        selectedPartId === partId ? "is-selected" : ""
      }`}
      data-part={id}
      onPointerEnter={handleHover}
      onPointerLeave={onLeave}
      onPointerMove={handleHover}
      onPointerDown={handleSelect}
    >
      {children}
    </g>
  );
}

function format(value: number) {
  return Number(value.toFixed(1));
}

function normalizeYaw(value: number) {
  let yaw = ((value + 180) % 360) - 180;
  if (yaw < -180) yaw += 360;
  return yaw;
}

function normalizePitch(value: number) {
  return clamp(value, -MAX_PITCH, MAX_PITCH);
}

function yawDistance(a: number, b: number) {
  return Math.abs(normalizeYaw(a - b));
}

function nearestPerspective(yawDeg: number) {
  return PERSPECTIVES.reduce((nearest, perspective) =>
    yawDistance(yawDeg, perspective.yawDeg) <
    yawDistance(yawDeg, nearest.yawDeg)
      ? perspective
      : nearest,
  );
}

function perspectiveFromCamera(yawDeg: number, pitchDeg: number): Perspective {
  const yaw = normalizeYaw(yawDeg);
  const pitch = normalizePitch(pitchDeg);
  const radians = (yaw / 180) * Math.PI;
  const nearest = nearestPerspective(yaw);

  return {
    headOffset: format(20 * Math.sin(radians)),
    key: `yaw-${Math.round(yaw)}-pitch-${Math.round(pitch)}`,
    label: nearest.label,
    pitchDeg: pitch,
    showFace: Math.cos(radians) > -0.28,
    yawDeg: yaw,
  };
}

function projectCameraPoint(
  localX: number,
  localY: number,
  localZ: number,
  perspective: Perspective,
): ProjectedPoint {
  const yawRadians = (perspective.yawDeg / 180) * Math.PI;
  const pitchRadians = (perspective.pitchDeg / 180) * Math.PI;
  const yawX = localX * Math.cos(yawRadians) + localZ * Math.sin(yawRadians);
  const yawZ = localZ * Math.cos(yawRadians) - localX * Math.sin(yawRadians);
  const pitchY =
    localY * Math.cos(pitchRadians) - yawZ * Math.sin(pitchRadians);
  const pitchZ =
    yawZ * Math.cos(pitchRadians) + localY * Math.sin(pitchRadians);

  return {
    depth: format(pitchZ),
    x: format(CENTER_X + yawX),
    y: format(CAMERA_PITCH_PIVOT_Y + pitchY),
  };
}

function projectHeadLocalPoint(
  localX: number,
  localY: number,
  localZ: number,
  headCx: number,
  headCy: number,
  perspective: Perspective,
): ProjectedPoint {
  const yawRadians = (perspective.yawDeg / 180) * Math.PI;
  const pitchRadians = (perspective.pitchDeg / 180) * Math.PI;
  const yawX = localX * Math.cos(yawRadians) + localZ * Math.sin(yawRadians);
  const yawZ = localZ * Math.cos(yawRadians) - localX * Math.sin(yawRadians);
  const pitchY =
    localY * Math.cos(pitchRadians) - yawZ * Math.sin(pitchRadians);
  const pitchZ =
    yawZ * Math.cos(pitchRadians) + localY * Math.sin(pitchRadians);

  return {
    depth: format(pitchZ),
    x: format(headCx + yawX),
    y: format(headCy + pitchY),
  };
}

function curvePath(start: Point, end: Point, bend = 0) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  const cx = (start.x + end.x) / 2 + (-dy / len) * bend;
  const cy = (start.y + end.y) / 2 + (dx / len) * bend;

  return `M${format(start.x)} ${format(start.y)}Q${format(cx)} ${format(
    cy,
  )} ${format(end.x)} ${format(end.y)}`;
}

function headPath(cx: number, cy: number) {
  return `M${format(cx - 1)} ${format(cy - 63)}c35-4 67 22 72 59 6 42-21 75-61 76-43 1-78-28-78-66 0-38 27-65 67-69Z`;
}

function headSurfacePoint(
  localX: number,
  y: number,
  headCx: number,
  headCy: number,
  perspective: Perspective,
  radius: number,
): ProjectedPoint {
  const clampedX = Math.max(-radius + 1, Math.min(radius - 1, localX));
  const localY = y - HEAD_CY;
  const surfaceDepth = Math.sqrt(radius ** 2 - clampedX ** 2);

  return projectHeadLocalPoint(
    clampedX,
    localY,
    surfaceDepth,
    headCx,
    headCy,
    perspective,
  );
}

function headPoint(
  localX: number,
  localY: number,
  localZ: number,
  headCx: number,
  headCy: number,
  perspective: Perspective,
): Point {
  const projected = projectHeadLocalPoint(
    localX,
    localY,
    localZ,
    headCx,
    headCy,
    perspective,
  );

  return { x: projected.x, y: projected.y };
}

function headPointDepth(
  localX: number,
  localY: number,
  localZ: number,
  perspective: Perspective,
) {
  const yawRadians = (perspective.yawDeg / 180) * Math.PI;
  const pitchRadians = (perspective.pitchDeg / 180) * Math.PI;
  const yawZ = localZ * Math.cos(yawRadians) - localX * Math.sin(yawRadians);

  return yawZ * Math.cos(pitchRadians) + localY * Math.sin(pitchRadians);
}

function hairPath(cx: number, cy: number, perspective: Perspective) {
  const p = (localX: number, localY: number, localZ: number) =>
    headPoint(localX, localY, localZ, cx, cy, perspective);
  const r0 = p(34, -52, -38);
  const r1 = p(42, -61, -49);
  const r2 = p(57, -58, -54);
  const r3 = p(64, -45, -48);
  const a1 = p(49, -70, -60);
  const a2 = p(70, -64, -67);
  const a3 = p(69, -47, -58);
  const b0 = p(46, -42, -45);
  const b1 = p(67, -46, -64);
  const b2 = p(78, -30, -58);
  const b3 = p(63, -18, -47);
  const c0 = p(44, -28, -44);
  const c1 = p(61, -18, -61);
  const c2 = p(56, -3, -48);
  const c3 = p(40, 4, -37);

  return [
    `M${r0.x} ${r0.y}C${r1.x} ${r1.y} ${r2.x} ${r2.y} ${r3.x} ${r3.y}`,
    `M${r1.x} ${r1.y}C${a1.x} ${a1.y} ${a2.x} ${a2.y} ${a3.x} ${a3.y}`,
    `M${b0.x} ${b0.y}C${b1.x} ${b1.y} ${b2.x} ${b2.y} ${b3.x} ${b3.y}`,
    `M${c0.x} ${c0.y}C${c1.x} ${c1.y} ${c2.x} ${c2.y} ${c3.x} ${c3.y}`,
  ].join("");
}

function hairSurfaceOpacity(perspective: Perspective) {
  return format(
    clamp((headPointDepth(54, -45, -52, perspective) - 6) / 36, 0, 1),
  );
}

function shoePath(ankle: Point, toeDir: number) {
  return `M${format(ankle.x)} ${format(ankle.y)}c${format(
    toeDir * 16,
  )} 2 ${format(toeDir * 30)} 10 ${format(
    toeDir * 35,
  )} 22c${format(toeDir * -14)} 7 ${format(
    toeDir * -32,
  )} 2 ${format(toeDir * -43)}-11c${format(
    toeDir * 5,
  )}-8 ${format(toeDir * 18)}-13 ${format(toeDir * 28)}-11Z`;
}

function shoeToeDirection(ankle: Point, knee: Point) {
  return ankle.x < knee.x ? -1 : 1;
}

function faceSurfacePoint(
  localX: number,
  y: number,
  headCx: number,
  headCy: number,
  perspective: Perspective,
): FaceSurfacePoint {
  const projected = headSurfacePoint(
    localX,
    y,
    headCx,
    headCy,
    perspective,
    FACE_RADIUS,
  );
  const visibility = Math.max(0, Math.min(1, projected.depth / FACE_RADIUS));

  return {
    ...projected,
    visibility,
    visible: perspective.showFace && visibility > FACE_EDGE_VISIBILITY,
  };
}

function faceFeaturePath(
  localPoints: Point[],
  headCx: number,
  headCy: number,
  perspective: Perspective,
) {
  const points = localPoints.map((point) =>
    faceSurfacePoint(point.x, point.y, headCx, headCy, perspective),
  );
  let path = "";
  let segmentOpen = false;
  let visibleCount = 0;

  points.forEach((point) => {
    if (!point.visible) {
      segmentOpen = false;
      return;
    }

    visibleCount += 1;
    path += `${segmentOpen ? "L" : "M"}${point.x} ${point.y}`;
    segmentOpen = true;
  });

  return visibleCount > 1 ? path : "";
}

function smilePoint(t: number): Point {
  const start = { x: -42, y: 98 };
  const controlA = { x: -24, y: 120 };
  const controlB = { x: 17, y: 120 };
  const end = { x: 42, y: 102 };
  const invT = 1 - t;

  return {
    x:
      invT ** 3 * start.x +
      3 * invT ** 2 * t * controlA.x +
      3 * invT * t ** 2 * controlB.x +
      t ** 3 * end.x,
    y:
      invT ** 3 * start.y +
      3 * invT ** 2 * t * controlA.y +
      3 * invT * t ** 2 * controlB.y +
      t ** 3 * end.y,
  };
}

function smilePath(headCx: number, headCy: number, perspective: Perspective) {
  return faceFeaturePath(
    Array.from({ length: 12 }, (_, index) => smilePoint(index / 11)),
    headCx,
    headCy,
    perspective,
  );
}

function eyebrowPath(headCx: number, headCy: number, perspective: Perspective) {
  return faceFeaturePath(
    [
      { x: 22, y: 47 },
      { x: 26, y: 49 },
      { x: 29, y: 54 },
      { x: 30, y: 60 },
    ],
    headCx,
    headCy,
    perspective,
  );
}

function projectPoint(
  point: Point,
  perspective: Perspective,
  depth = 0,
): ProjectedPoint {
  return projectCameraPoint(
    point.x - CENTER_X,
    point.y - CAMERA_PITCH_PIVOT_Y,
    depth,
    perspective,
  );
}

function projectSkeleton(pose: Pose, perspective: Perspective) {
  const { depth = {}, skeleton } = pose;

  return {
    elbowL: projectPoint(skeleton.elbowL, perspective, depth.elbowL),
    elbowR: projectPoint(skeleton.elbowR, perspective, depth.elbowR),
    footL: projectPoint(skeleton.footL, perspective, depth.footL),
    footR: projectPoint(skeleton.footR, perspective, depth.footR),
    hip: projectPoint(skeleton.hip, perspective, depth.hip),
    kneeL: projectPoint(skeleton.kneeL, perspective, depth.kneeL),
    kneeR: projectPoint(skeleton.kneeR, perspective, depth.kneeR),
    neck: projectPoint(skeleton.neck, perspective, depth.neck),
    palmL: projectPoint(skeleton.palmL, perspective, depth.palmL),
    palmR: projectPoint(skeleton.palmR, perspective, depth.palmR),
    shoulderL: projectPoint(
      skeleton.shoulderL,
      perspective,
      depth.shoulderL,
    ),
    shoulderR: projectPoint(
      skeleton.shoulderR,
      perspective,
      depth.shoulderR,
    ),
  };
}

function cloneSkeleton(skeleton: Skeleton): Skeleton {
  return SKELETON_KEYS.reduce(
    (copy, key) => ({
      ...copy,
      [key]: { ...skeleton[key] },
    }),
    {} as Skeleton,
  );
}

function cloneDepth(depth: SkeletonDepth = {}): SkeletonDepth {
  return { ...depth };
}

function spatialPoint(
  skeleton: Skeleton,
  depth: SkeletonDepth,
  key: keyof Skeleton,
): SpatialPoint {
  return {
    ...skeleton[key],
    z: depth[key] ?? 0,
  };
}

function spatialDistance(a: SpatialPoint, b: SpatialPoint) {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function clampSpatialPoint(point: SpatialPoint): SpatialPoint {
  return {
    x: format(clamp(point.x, 42, VIEWBOX_W - 42)),
    y: format(clamp(point.y, 42, VIEWBOX_H - 38)),
    z: format(clamp(point.z, -DEPTH_LIMIT, DEPTH_LIMIT)),
  };
}

function writeSpatialPoint(
  skeleton: Skeleton,
  depth: SkeletonDepth,
  key: keyof Skeleton,
  point: SpatialPoint,
) {
  const nextPoint = clampSpatialPoint(point);
  skeleton[key] = { x: nextPoint.x, y: nextPoint.y };
  depth[key] = nextPoint.z;
}

function moveSkeletonKeys(
  skeleton: Skeleton,
  depth: SkeletonDepth,
  keys: Array<keyof Skeleton>,
  delta: SpatialDelta,
) {
  const next = cloneSkeleton(skeleton);
  const nextDepth = cloneDepth(depth);

  keys.forEach((key) => {
    writeSpatialPoint(next, nextDepth, key, {
      x: skeleton[key].x + delta.x,
      y: skeleton[key].y + delta.y,
      z: (depth[key] ?? 0) + delta.z,
    });
  });

  return { depth: nextDepth, skeleton: next };
}

function addSpatial(point: SpatialPoint, delta: SpatialDelta): SpatialPoint {
  return {
    x: point.x + delta.x,
    y: point.y + delta.y,
    z: point.z + delta.z,
  };
}

function subtractSpatial(a: SpatialPoint, b: SpatialPoint): SpatialDelta {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z,
  };
}

function scaleSpatial(vector: SpatialDelta, scale: number): SpatialDelta {
  return {
    x: vector.x * scale,
    y: vector.y * scale,
    z: vector.z * scale,
  };
}

function dotSpatial(a: SpatialDelta, b: SpatialDelta) {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function lengthSpatial(vector: SpatialDelta) {
  return Math.hypot(vector.x, vector.y, vector.z);
}

function normalizeSpatial(vector: SpatialDelta): SpatialDelta {
  const length = lengthSpatial(vector) || 1;

  return {
    x: vector.x / length,
    y: vector.y / length,
    z: vector.z / length,
  };
}

function spatialFromVector(
  point: SpatialPoint,
  vector: SpatialDelta,
): SpatialPoint {
  return {
    x: point.x + vector.x,
    y: point.y + vector.y,
    z: point.z + vector.z,
  };
}

function twoBoneJointCandidate(
  root: SpatialPoint,
  end: SpatialPoint,
  upperLength: number,
  lowerLength: number,
  preferredJoint: SpatialPoint,
) {
  const rootToEnd = subtractSpatial(end, root);
  const distance = lengthSpatial(rootToEnd) || 1;
  const unit = normalizeSpatial(rootToEnd);
  const along =
    (upperLength ** 2 - lowerLength ** 2 + distance ** 2) / (2 * distance);
  const height = Math.sqrt(Math.max(0, upperLength ** 2 - along ** 2));
  const base = spatialFromVector(root, scaleSpatial(unit, along));
  const preferredVector = subtractSpatial(preferredJoint, base);
  const preferredDot = dotSpatial(preferredVector, unit);
  const planeVector = {
    x: preferredVector.x - unit.x * preferredDot,
    y: preferredVector.y - unit.y * preferredDot,
    z: preferredVector.z - unit.z * preferredDot,
  };
  const direction =
    lengthSpatial(planeVector) > 0.01
      ? normalizeSpatial(planeVector)
      : { x: -unit.y, y: unit.x, z: 0 };

  return clampSpatialPoint(
    spatialFromVector(base, scaleSpatial(direction, height)),
  );
}

function solveTwoBone(
  skeleton: Skeleton,
  depth: SkeletonDepth,
  operation: Extract<EditOperation, { kind: "chain" }>,
  target: SpatialPoint,
  preferredJoint = spatialPoint(skeleton, depth, operation.joint),
) {
  const next = cloneSkeleton(skeleton);
  const nextDepth = cloneDepth(depth);
  const root = spatialPoint(skeleton, depth, operation.root);
  const joint = spatialPoint(skeleton, depth, operation.joint);
  const end = spatialPoint(skeleton, depth, operation.end);
  const upperLength = spatialDistance(root, joint);
  const lowerLength = spatialDistance(joint, end);
  const targetVector = subtractSpatial(target, root);
  const targetDistance = lengthSpatial(targetVector) || 1;
  const maxReach = Math.max(upperLength + lowerLength - 0.01, 1);
  const minReach = Math.max(Math.abs(upperLength - lowerLength) + 0.01, 1);
  const solvedDistance = clamp(targetDistance, minReach, maxReach);
  const direction = normalizeSpatial(targetVector);
  const solvedEnd = clampSpatialPoint(
    spatialFromVector(root, scaleSpatial(direction, solvedDistance)),
  );
  const solvedJoint = twoBoneJointCandidate(
    root,
    solvedEnd,
    upperLength,
    lowerLength,
    preferredJoint,
  );

  writeSpatialPoint(next, nextDepth, operation.joint, solvedJoint);
  writeSpatialPoint(next, nextDepth, operation.end, solvedEnd);
  return { depth: nextDepth, skeleton: next };
}

function solveBendControl(
  skeleton: Skeleton,
  depth: SkeletonDepth,
  operation: Extract<EditOperation, { kind: "bend" }>,
  target: SpatialPoint,
) {
  const next = cloneSkeleton(skeleton);
  const nextDepth = cloneDepth(depth);
  const root = spatialPoint(skeleton, depth, operation.root);
  const joint = spatialPoint(skeleton, depth, operation.joint);
  const end = spatialPoint(skeleton, depth, operation.end);
  const upperLength = spatialDistance(root, joint);
  const lowerLength = spatialDistance(joint, end);

  writeSpatialPoint(
    next,
    nextDepth,
    operation.joint,
    twoBoneJointCandidate(root, end, upperLength, lowerLength, target),
  );

  return { depth: nextDepth, skeleton: next };
}

function directEditOperationForPart(partId: string): EditOperation | null {
  switch (partId) {
    case "left-palm":
      return { kind: "move", keys: ["palmL"], target: "palmL" };
    case "left-forearm":
      return { kind: "move", keys: ["elbowL", "palmL"], target: "elbowL" };
    case "right-palm":
      return { kind: "move", keys: ["palmR"], target: "palmR" };
    case "right-forearm":
      return { kind: "move", keys: ["elbowR", "palmR"], target: "elbowR" };
    case "left-shoe":
      return { kind: "move", keys: ["footL"], target: "footL" };
    case "left-shin":
      return { kind: "move", keys: ["kneeL", "footL"], target: "kneeL" };
    case "right-shoe":
      return { kind: "move", keys: ["footR"], target: "footR" };
    case "right-shin":
      return { kind: "move", keys: ["kneeR", "footR"], target: "kneeR" };
    case "left-elbow":
      return { kind: "move", keys: ["elbowL"], target: "elbowL" };
    case "left-upper-arm":
      return { kind: "move", keys: ["elbowL", "palmL"], target: "elbowL" };
    case "right-elbow":
      return { kind: "move", keys: ["elbowR"], target: "elbowR" };
    case "right-upper-arm":
      return { kind: "move", keys: ["elbowR", "palmR"], target: "elbowR" };
    case "left-knee":
      return { kind: "move", keys: ["kneeL"], target: "kneeL" };
    case "left-thigh":
      return { kind: "move", keys: ["kneeL", "footL"], target: "kneeL" };
    case "right-knee":
      return { kind: "move", keys: ["kneeR"], target: "kneeR" };
    case "right-thigh":
      return { kind: "move", keys: ["kneeR", "footR"], target: "kneeR" };
    case "left-shoulder":
      return {
        kind: "move",
        keys: ["shoulderL", "elbowL", "palmL"],
        target: "shoulderL",
      };
    case "right-shoulder":
      return {
        kind: "move",
        keys: ["shoulderR", "elbowR", "palmR"],
        target: "shoulderR",
      };
    case "neck":
    case "head":
    case "hair":
    case "left-eye":
    case "right-eye":
    case "eyebrow":
    case "lips":
      return {
        kind: "move",
        keys: [
          "neck",
          "shoulderL",
          "shoulderR",
          "elbowL",
          "elbowR",
          "palmL",
          "palmR",
        ],
        target: "neck",
      };
    case "torso":
    case "hip":
      return {
        kind: "move",
        keys: ["hip", "kneeL", "kneeR", "footL", "footR"],
        target: "hip",
      };
    default:
      return null;
  }
}

function assistedEditOperationForPart(partId: string): EditOperation | null {
  switch (partId) {
    case "left-palm":
    case "left-forearm":
      return { end: "palmL", joint: "elbowL", kind: "chain", root: "shoulderL" };
    case "right-palm":
    case "right-forearm":
      return { end: "palmR", joint: "elbowR", kind: "chain", root: "shoulderR" };
    case "left-shoe":
    case "left-shin":
      return { end: "footL", joint: "kneeL", kind: "chain", root: "hip" };
    case "right-shoe":
    case "right-shin":
      return { end: "footR", joint: "kneeR", kind: "chain", root: "hip" };
    case "left-elbow":
      return {
        end: "palmL",
        joint: "elbowL",
        kind: "bend",
        root: "shoulderL",
      };
    case "left-upper-arm":
      return { kind: "move", keys: ["elbowL", "palmL"], target: "elbowL" };
    case "right-elbow":
      return {
        end: "palmR",
        joint: "elbowR",
        kind: "bend",
        root: "shoulderR",
      };
    case "right-upper-arm":
      return { kind: "move", keys: ["elbowR", "palmR"], target: "elbowR" };
    case "left-knee":
      return { end: "footL", joint: "kneeL", kind: "bend", root: "hip" };
    case "left-thigh":
      return { kind: "move", keys: ["kneeL", "footL"], target: "kneeL" };
    case "right-knee":
      return { end: "footR", joint: "kneeR", kind: "bend", root: "hip" };
    case "right-thigh":
      return { kind: "move", keys: ["kneeR", "footR"], target: "kneeR" };
    case "left-shoulder":
      return {
        kind: "move",
        keys: ["shoulderL", "elbowL", "palmL"],
        target: "shoulderL",
      };
    case "right-shoulder":
      return {
        kind: "move",
        keys: ["shoulderR", "elbowR", "palmR"],
        target: "shoulderR",
      };
    case "neck":
    case "head":
    case "hair":
    case "left-eye":
    case "right-eye":
    case "eyebrow":
    case "lips":
      return {
        kind: "move",
        keys: [
          "neck",
          "shoulderL",
          "shoulderR",
          "elbowL",
          "elbowR",
          "palmL",
          "palmR",
        ],
        target: "neck",
      };
    case "torso":
    case "hip":
      return {
        kind: "move",
        keys: ["hip", "kneeL", "kneeR", "footL", "footR"],
        target: "hip",
      };
    default:
      return null;
  }
}

function editOperationForPart(
  partId: string,
  directDrag: boolean,
): EditOperation | null {
  return directDrag
    ? directEditOperationForPart(partId)
    : assistedEditOperationForPart(partId);
}

function skeletonDeltaFromProjectedDrag(
  startPointer: Point,
  pointer: Point,
  perspective: Perspective,
): SpatialDelta {
  const radians = (perspective.yawDeg / 180) * Math.PI;
  const screenX = pointer.x - startPointer.x;

  return {
    x: screenX * Math.cos(radians),
    y: pointer.y - startPointer.y,
    z: screenX * Math.sin(radians),
  };
}

function applyEditOperation(
  skeleton: Skeleton,
  depth: SkeletonDepth,
  operation: EditOperation,
  delta: SpatialDelta,
) {
  if (operation.kind === "bend") {
    return solveBendControl(
      skeleton,
      depth,
      operation,
      addSpatial(spatialPoint(skeleton, depth, operation.joint), delta),
    );
  }

  if (operation.kind === "chain") {
    return solveTwoBone(
      skeleton,
      depth,
      operation,
      addSpatial(spatialPoint(skeleton, depth, operation.end), delta),
      addSpatial(spatialPoint(skeleton, depth, operation.joint), delta),
    );
  }

  return moveSkeletonKeys(skeleton, depth, operation.keys, delta);
}

function PartGroup({
  children,
  controls,
  frame,
  interactive = false,
  label,
  partId,
}: PartGroupProps) {
  const id = `${frame.id}-${partId}`;

  if (!interactive || !controls) {
    return (
      <g aria-label={label} className="stickman-part is-static" data-part={id}>
        {children}
      </g>
    );
  }

  return (
    <StickmanPart
      activeId={controls.activeId}
      editing={controls.editing}
      id={id}
      label={label}
      onHover={controls.onHover}
      onLeave={controls.onLeave}
      onSelect={controls.onSelect}
      partId={partId}
      selectedPartId={controls.selectedPartId}
    >
      {children}
    </StickmanPart>
  );
}

function SegmentPart({
  bend = 0,
  controls,
  end,
  frame,
  interactive,
  label,
  partId,
  start,
  width = 3.4,
}: FigureProps & {
  bend?: number;
  end: Point;
  label: string;
  partId: string;
  start: Point;
  width?: number;
}) {
  const d = curvePath(start, end, bend);

  return (
    <PartGroup
      controls={controls}
      frame={frame}
      interactive={interactive}
      label={label}
      partId={partId}
    >
      <path className="stickman-hit" d={d} strokeWidth="19" />
      <path className="stickman-line" d={d} strokeWidth={width} />
    </PartGroup>
  );
}

function JointPart({
  center,
  controls,
  frame,
  interactive,
  label,
  partId,
}: FigureProps & {
  center: Point;
  label: string;
  partId: string;
}) {
  const hitRadius = controls?.editing ? 11 : 18;

  return (
    <PartGroup
      controls={controls}
      frame={frame}
      interactive={interactive}
      label={label}
      partId={partId}
    >
      <circle className="stickman-joint" cx={center.x} cy={center.y} r="8" />
      <circle
        className="stickman-hit"
        cx={center.x}
        cy={center.y}
        r={hitRadius}
      />
    </PartGroup>
  );
}

function PalmPart({
  center,
  controls,
  frame,
  interactive,
  label,
  partId,
}: FigureProps & {
  center: Point;
  label: string;
  partId: string;
}) {
  return (
    <PartGroup
      controls={controls}
      frame={frame}
      interactive={interactive}
      label={label}
      partId={partId}
    >
      <ellipse
        className="stickman-hit"
        cx={center.x}
        cy={center.y}
        fill="transparent"
        rx="18"
        ry="23"
        stroke="none"
      />
      <ellipse
        className="stickman-line"
        cx={center.x}
        cy={center.y}
        rx="10.5"
        ry="16"
        strokeWidth="3"
      />
    </PartGroup>
  );
}

function EditorHandle({
  center,
  controls,
  frame,
  interactive,
  label,
  partId,
}: FigureProps & {
  center: Point;
  label: string;
  partId: string;
}) {
  if (!controls?.editing) return null;

  return (
    <PartGroup
      controls={controls}
      frame={frame}
      interactive={interactive}
      label={label}
      partId={partId}
    >
      <circle
        className="stickman-editor-handle"
        cx={center.x}
        cy={center.y}
        r="7"
      />
      <circle className="stickman-hit" cx={center.x} cy={center.y} r="11" />
    </PartGroup>
  );
}

function StickmanFigure({
  controls,
  frame,
  interactive = false,
}: FigureProps) {
  const points = projectSkeleton(frame.pose, frame.perspective);
  const projectedHeadCenter = projectPoint(
    { x: CENTER_X, y: HEAD_CY },
    frame.perspective,
  );
  const headCx = CENTER_X + frame.perspective.headOffset;
  const headCy = projectedHeadCenter.y;
  const neckTop = headPoint(0, 63, 0, headCx, headCy, frame.perspective);
  const toeL = shoeToeDirection(points.footL, points.kneeL);
  const toeR = shoeToeDirection(points.footR, points.kneeR);
  const partProps = { controls, frame, interactive };
  const leftEye = faceSurfacePoint(-18, 65, headCx, headCy, frame.perspective);
  const rightEye = faceSurfacePoint(10, 67, headCx, headCy, frame.perspective);
  const eyeTilt = format(
    18 * Math.cos((frame.perspective.yawDeg / 180) * Math.PI),
  );
  const leftEyeRx = format(3.5 + leftEye.visibility * 4.7);
  const rightEyeRx = format(3.5 + rightEye.visibility * 4.7);
  const browPath = eyebrowPath(headCx, headCy, frame.perspective);
  const hairD = hairPath(headCx, headCy, frame.perspective);
  const hairSurface = hairSurfaceOpacity(frame.perspective);
  const hairEdge = format(1 - hairSurface);
  const lipPath = smilePath(headCx, headCy, frame.perspective);

  return (
    <g fill="none" stroke="#111" strokeLinecap="round" strokeLinejoin="round">
      <PartGroup {...partProps} label="hair" partId="hair">
        <path
          className="stickman-hit"
          d={hairD}
          opacity={hairEdge}
          strokeWidth="18"
        />
        <path
          className="stickman-line"
          d={hairD}
          opacity={hairEdge}
          strokeWidth="3.4"
        />
      </PartGroup>

      <PartGroup
        {...partProps}
        label="head"
        partId="head"
      >
        <path
          className="stickman-hit"
          d={headPath(headCx, headCy)}
          strokeWidth="24"
        />
        <path
          d={headPath(headCx, headCy)}
          fill="#fff"
          pointerEvents="none"
          stroke="none"
        />
        <path
          className="stickman-line"
          d={headPath(headCx, headCy)}
          strokeWidth="3.4"
        />
      </PartGroup>

      {hairSurface > 0 && (
        <PartGroup {...partProps} label="hair" partId="hair">
          <path
            className="stickman-hit"
            d={hairD}
            opacity={hairSurface}
            strokeWidth="18"
          />
          <path
            className="stickman-line"
            d={hairD}
            opacity={hairSurface}
            strokeWidth="3.4"
          />
        </PartGroup>
      )}

      {leftEye.visible && (
        <PartGroup {...partProps} label="left eye" partId="left-eye">
          <ellipse
            className="stickman-hit"
            cx={leftEye.x}
            cy={leftEye.y}
            fill="transparent"
            rx={leftEyeRx + 7}
            ry="21"
            stroke="none"
            transform={`rotate(${eyeTilt} ${leftEye.x} ${leftEye.y})`}
          />
          <ellipse
            className="stickman-fill"
            cx={leftEye.x}
            cy={leftEye.y}
            fill="#050505"
            rx={leftEyeRx}
            ry="14.2"
            stroke="none"
            transform={`rotate(${eyeTilt} ${leftEye.x} ${leftEye.y})`}
          />
        </PartGroup>
      )}

      {rightEye.visible && (
        <PartGroup {...partProps} label="right eye" partId="right-eye">
          <ellipse
            className="stickman-hit"
            cx={rightEye.x}
            cy={rightEye.y}
            fill="transparent"
            rx={rightEyeRx + 7}
            ry="21"
            stroke="none"
            transform={`rotate(${eyeTilt} ${rightEye.x} ${rightEye.y})`}
          />
          <ellipse
            className="stickman-fill"
            cx={rightEye.x}
            cy={rightEye.y}
            fill="#050505"
            rx={rightEyeRx}
            ry="14"
            stroke="none"
            transform={`rotate(${eyeTilt} ${rightEye.x} ${rightEye.y})`}
          />
        </PartGroup>
      )}

      {browPath && (
        <PartGroup {...partProps} label="eyebrow" partId="eyebrow">
          <path className="stickman-hit" d={browPath} strokeWidth="16" />
          <path className="stickman-line" d={browPath} strokeWidth="3.4" />
        </PartGroup>
      )}

      {lipPath && (
        <PartGroup {...partProps} label="lips" partId="lips">
          <path className="stickman-hit" d={lipPath} strokeWidth="18" />
          <path className="stickman-line" d={lipPath} strokeWidth="2.6" />
        </PartGroup>
      )}

      <SegmentPart
        {...partProps}
        bend={3}
        end={points.neck}
        label="neck"
        partId="neck"
        start={neckTop}
      />
      <SegmentPart
        {...partProps}
        bend={-4}
        end={points.hip}
        label="torso"
        partId="torso"
        start={points.neck}
      />

      <SegmentPart
        {...partProps}
        bend={0}
        end={points.shoulderL}
        label="left shoulder"
        partId="left-shoulder"
        start={points.neck}
        width={3.2}
      />
      <SegmentPart
        {...partProps}
        bend={0}
        end={points.shoulderR}
        label="right shoulder"
        partId="right-shoulder"
        start={points.neck}
        width={3.2}
      />

      <SegmentPart
        {...partProps}
        bend={-5}
        end={points.elbowL}
        label="left upper arm"
        partId="left-upper-arm"
        start={points.shoulderL}
      />
      <SegmentPart
        {...partProps}
        bend={4}
        end={points.palmL}
        label="left forearm"
        partId="left-forearm"
        start={points.elbowL}
      />
      <JointPart
        {...partProps}
        center={points.elbowL}
        label="left elbow"
        partId="left-elbow"
      />
      <PalmPart
        {...partProps}
        center={points.palmL}
        label="left palm"
        partId="left-palm"
      />

      <SegmentPart
        {...partProps}
        bend={5}
        end={points.elbowR}
        label="right upper arm"
        partId="right-upper-arm"
        start={points.shoulderR}
      />
      <SegmentPart
        {...partProps}
        bend={-4}
        end={points.palmR}
        label="right forearm"
        partId="right-forearm"
        start={points.elbowR}
      />
      <JointPart
        {...partProps}
        center={points.elbowR}
        label="right elbow"
        partId="right-elbow"
      />
      <PalmPart
        {...partProps}
        center={points.palmR}
        label="right palm"
        partId="right-palm"
      />

      <SegmentPart
        {...partProps}
        bend={6}
        end={points.kneeL}
        label="left thigh"
        partId="left-thigh"
        start={points.hip}
      />
      <SegmentPart
        {...partProps}
        bend={-5}
        end={points.footL}
        label="left shin"
        partId="left-shin"
        start={points.kneeL}
      />
      <JointPart
        {...partProps}
        center={points.kneeL}
        label="left knee"
        partId="left-knee"
      />

      <SegmentPart
        {...partProps}
        bend={-6}
        end={points.kneeR}
        label="right thigh"
        partId="right-thigh"
        start={points.hip}
      />
      <SegmentPart
        {...partProps}
        bend={5}
        end={points.footR}
        label="right shin"
        partId="right-shin"
        start={points.kneeR}
      />
      <JointPart
        {...partProps}
        center={points.kneeR}
        label="right knee"
        partId="right-knee"
      />

      <PartGroup {...partProps} label="left shoe" partId="left-shoe">
        <path
          className="stickman-hit"
          d={shoePath(points.footL, toeL)}
          strokeWidth="16"
        />
        <path
          className="stickman-line"
          d={shoePath(points.footL, toeL)}
          strokeWidth="3"
        />
      </PartGroup>

      <PartGroup {...partProps} label="right shoe" partId="right-shoe">
        <path
          className="stickman-hit"
          d={shoePath(points.footR, toeR)}
          strokeWidth="16"
        />
        <path
          className="stickman-line"
          d={shoePath(points.footR, toeR)}
          strokeWidth="3"
        />
      </PartGroup>

      <EditorHandle
        {...partProps}
        center={points.neck}
        label="neck"
        partId="neck"
      />
      <EditorHandle
        {...partProps}
        center={points.shoulderL}
        label="left shoulder"
        partId="left-shoulder"
      />
      <EditorHandle
        {...partProps}
        center={points.shoulderR}
        label="right shoulder"
        partId="right-shoulder"
      />
      <EditorHandle
        {...partProps}
        center={points.hip}
        label="hip"
        partId="hip"
      />
      <EditorHandle
        {...partProps}
        center={points.palmL}
        label="left palm"
        partId="left-palm"
      />
      <EditorHandle
        {...partProps}
        center={points.palmR}
        label="right palm"
        partId="right-palm"
      />
      <EditorHandle
        {...partProps}
        center={points.footL}
        label="left shoe"
        partId="left-shoe"
      />
      <EditorHandle
        {...partProps}
        center={points.footR}
        label="right shoe"
        partId="right-shoe"
      />
    </g>
  );
}

function FrameSvg({ controls, frame, interactive = false }: FigureProps) {
  return (
    <svg
      className="stickman-canvas"
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      role="img"
      aria-label={`${frame.perspective.label} ${frame.pose.label} stickman`}
    >
      <rect width={VIEWBOX_W} height={VIEWBOX_H} fill="#fff" />
      <StickmanFigure
        controls={controls}
        frame={frame}
        interactive={interactive}
      />
    </svg>
  );
}

export function StickmanCanvas() {
  const [poseEdits, setPoseEdits] = useState<Record<string, PoseEdit>>({});
  const [sheetVisible, setSheetVisible] = useState(true);
  const editablePoses = useMemo(
    () =>
      POSES.map((pose) => ({
        ...pose,
        depth: {
          ...(pose.depth ?? {}),
          ...(poseEdits[pose.key]?.depth ?? {}),
        },
        skeleton: poseEdits[pose.key]?.skeleton ?? pose.skeleton,
      })),
    [poseEdits],
  );
  const frames = useMemo(
    () =>
      PERSPECTIVES.flatMap((perspective) =>
        editablePoses.map((pose) => ({
          id: `${perspective.key}-${pose.key}`,
          perspective,
          pose,
        })),
      ),
    [editablePoses],
  );
  const artboardRef = useRef<HTMLDivElement | null>(null);
  const [selectedPoseKey, setSelectedPoseKey] = useState("run-a");
  const [yawDeg, setYawDeg] = useState(90);
  const [pitchDeg, setPitchDeg] = useState(0);
  const [hoveredPart, setHoveredPart] = useState<HoveredPart | null>(null);
  const [selectedPart, setSelectedPart] = useState<SelectedPart | null>(null);
  const editDragRef = useRef<EditDrag | null>(null);
  const selectedPose =
    editablePoses.find((pose) => pose.key === selectedPoseKey) ??
    editablePoses[0];
  const selectedBasePose =
    POSES.find((pose) => pose.key === selectedPoseKey) ?? POSES[0];
  const selectedPerspective = perspectiveFromCamera(yawDeg, pitchDeg);
  const nearestSelectedPerspective = nearestPerspective(
    selectedPerspective.yawDeg,
  );
  const selectedFrame: CharacterFrame = {
    id: `turntable-${selectedPose.key}-${Math.round(
      selectedPerspective.yawDeg,
    )}-${Math.round(selectedPerspective.pitchDeg)}`,
    perspective: selectedPerspective,
    pose: selectedPose,
  };

  const handlePartHover: PartHoverHandler = (id, label, event) => {
    setHoveredPart({ id, label, x: event.clientX, y: event.clientY });
  };

  const pointerToSvgPoint = (event: PointerEvent<Element>): Point | null => {
    const svg = artboardRef.current?.querySelector("svg");
    const rect = svg?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;

    return {
      x: ((event.clientX - rect.left) / rect.width) * VIEWBOX_W,
      y: ((event.clientY - rect.top) / rect.height) * VIEWBOX_H,
    };
  };

  const updateSelectedPoseEdit = (edit: PoseEdit) => {
    setPoseEdits((current) => ({
      ...current,
      [selectedPose.key]: edit,
    }));
  };

  const handlePartSelect: PartSelectHandler = (partId, label, event) => {
    if (event.shiftKey) return;

    setSelectedPart({ label, partId });
    setHoveredPart({
      id: `${selectedFrame.id}-${partId}`,
      label,
      x: event.clientX,
      y: event.clientY,
    });

    const startPointer = pointerToSvgPoint(event);
    if (!startPointer) return;

    editDragRef.current = {
      partId,
      pointerId: event.pointerId,
      startDepth: cloneDepth(selectedPose.depth),
      startPointer,
      startSkeleton: cloneSkeleton(selectedPose.skeleton),
    };
  };

  const controls: PoseControls = {
    activeId: hoveredPart?.id ?? null,
    editing: true,
    onHover: handlePartHover,
    onLeave: () => setHoveredPart(null),
    onSelect: handlePartSelect,
    selectedPartId: selectedPart?.partId ?? null,
  };

  const handlePoseDrag = (event: PointerEvent<HTMLDivElement>) => {
    const editDrag = editDragRef.current;
    if (editDrag?.pointerId !== event.pointerId) return;

    const pointer = pointerToSvgPoint(event);
    if (!pointer) return;

    const operation = editOperationForPart(editDrag.partId, event.ctrlKey);
    if (!operation) return;

    const delta = skeletonDeltaFromProjectedDrag(
      editDrag.startPointer,
      pointer,
      selectedPerspective,
    );
    updateSelectedPoseEdit(
      applyEditOperation(
        editDrag.startSkeleton,
        editDrag.startDepth,
        operation,
        delta,
      ),
    );
  };

  const stopPoseDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (editDragRef.current?.pointerId === event.pointerId) {
      editDragRef.current = null;
    }
  };

  const handleCameraWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const unit = event.deltaMode === 1 ? 12 : event.deltaMode === 2 ? 80 : 1;
    const deltaX = event.deltaX * unit;
    const deltaY = event.deltaY * unit;

    setYawDeg((current) => normalizeYaw(current + deltaX * 0.18));
    setPitchDeg((current) => normalizePitch(current + deltaY * 0.12));
    setHoveredPart(null);
  };

  const resetSelectedPose = () => {
    setPoseEdits((current) => {
      const next = { ...current };
      delete next[selectedBasePose.key];
      return next;
    });
    setSelectedPart(null);
    setHoveredPart(null);
  };

  return (
    <div className={`stickman-shell ${sheetVisible ? "" : "is-sheet-hidden"}`}>
      <section className="selected-panel" aria-label="Selected character view">
        <div className="selected-header">
          <span>
            {selectedFrame.perspective.label} · Y{" "}
            {Math.round(selectedFrame.perspective.yawDeg)}° · P{" "}
            {Math.round(selectedFrame.perspective.pitchDeg)}°
          </span>
          <strong>{selectedFrame.pose.label}</strong>
        </div>
        <div className="editor-toolbar" aria-label="Pose editor controls">
          <button
            className="reset-button"
            onClick={resetSelectedPose}
            type="button"
          >
            Reset pose
          </button>
          <button
            aria-pressed={sheetVisible}
            className="sheet-toggle-button"
            onClick={() => setSheetVisible((visible) => !visible)}
            type="button"
          >
            {sheetVisible ? "Hide sheet" : "Show sheet"}
          </button>
          <span className="selected-part-label">
            {selectedPart?.label ?? "No part selected"}
          </span>
        </div>
        <div
          className={`selected-artboard ${
            hoveredPart || selectedPart ? "has-rig-focus" : ""
          }`}
          ref={artboardRef}
          onContextMenu={(event) => event.preventDefault()}
          onPointerCancel={stopPoseDrag}
          onPointerMove={handlePoseDrag}
          onPointerUp={stopPoseDrag}
          onWheel={handleCameraWheel}
        >
          <FrameSvg controls={controls} frame={selectedFrame} interactive />
        </div>
      </section>

      {sheetVisible && (
        <section className="pose-browser" aria-label="Character pose grid">
          <div className="browser-header">
            <strong>Character sheet</strong>
            <span>{frames.length} views</span>
          </div>
          <div className="pose-grid">
            {frames.map((frame) => (
              <button
                aria-pressed={
                  frame.pose.key === selectedPose.key &&
                  frame.perspective.key === nearestSelectedPerspective.key
                }
                className={`pose-tile ${
                  frame.pose.key === selectedPose.key &&
                  frame.perspective.key === nearestSelectedPerspective.key
                    ? "is-selected"
                    : ""
                }`}
                key={frame.id}
                onClick={() => {
                  setSelectedPoseKey(frame.pose.key);
                  setYawDeg(frame.perspective.yawDeg);
                  setPitchDeg(0);
                  setSelectedPart(null);
                  setHoveredPart(null);
                }}
                type="button"
              >
                <FrameSvg frame={frame} />
                <span className="pose-label">{frame.pose.label}</span>
                <span className="perspective-label">
                  {frame.perspective.label}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {hoveredPart && (
        <div
          className="part-tooltip"
          style={{ left: hoveredPart.x + 14, top: hoveredPart.y + 14 }}
        >
          {hoveredPart.label}
        </div>
      )}
    </div>
  );
}
